import React, { useState, useMemo, useCallback } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Switch,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Progress,
  useToast,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FiSearch,
  FiEdit2,
  FiSettings,
  FiActivity,
  FiUploadCloud,
  FiToggleRight,
  FiToggleLeft,
  FiMoreVertical,
  FiUserMinus,
  FiUserPlus,
  FiRefreshCw,
} from 'react-icons/fi';
import { useCrmUsers } from '../../../hooks/useCrmUsers';
import { useTemplateSubscribers } from '../../../hooks/useTemplateSubscribers';
import { getTriggerDescription } from '../../../components/CRM/Campaigns/campaignConstants';

function StatCard({ label, value, color }) {
  return (
    <Stat
      px={4}
      py={3}
      borderWidth="1px"
      borderRadius="lg"
      textAlign="center"
      minW="0"
    >
      <StatLabel fontSize="xs" isTruncated>{label}</StatLabel>
      <StatNumber fontSize="xl" color={color}>{value ?? '—'}</StatNumber>
    </Stat>
  );
}

export default function TemplateManagementDrawer({
  isOpen,
  onClose,
  template,
  onViewLogs,
  onSettings,
}) {
  const toast = useToast();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const subText = useColorModeValue('gray.600', 'gray.400');
  const selectionBg = useColorModeValue('blue.50', 'blue.900');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const {
    users: crmUsers,
    loading: usersLoading,
    userMap,
  } = useCrmUsers();

  const {
    subscribers,
    loading: subsLoading,
    error: subsError,
    reload: reloadSubs,
    busyIds,
    bulkProgress,
    subscribeUser,
    toggleUser,
    unsubscribeUser,
    bulkSubscribe,
    bulkToggle,
    cancelBulk,
  } = useTemplateSubscribers(isOpen ? template?.id : null);

  const subsByUserId = useMemo(() => {
    const m = new Map();
    subscribers.forEach((s) => m.set(s.userId, s));
    return m;
  }, [subscribers]);

  const mergedUsers = useMemo(() => {
    return crmUsers.map((u) => {
      const sub = subsByUserId.get(u.uid);
      return {
        ...u,
        subscription: sub || null,
        status: sub ? (sub.isEnabled ? 'enabled' : 'disabled') : 'not_subscribed',
      };
    });
  }, [crmUsers, subsByUserId]);

  const filtered = useMemo(() => {
    let list = mergedUsers;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter((u) => u.status === statusFilter);
    }
    return list;
  }, [mergedUsers, search, statusFilter]);

  const counts = useMemo(() => {
    let enabled = 0, disabled = 0, notSubscribed = 0;
    mergedUsers.forEach((u) => {
      if (u.status === 'enabled') enabled++;
      else if (u.status === 'disabled') disabled++;
      else notSubscribed++;
    });
    return { enabled, disabled, notSubscribed, total: mergedUsers.length };
  }, [mergedUsers]);

  const loading = usersLoading || subsLoading;

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((u) => u.uid)));
    }
  }, [filtered, selectedIds]);

  const toggleSelect = useCallback((uid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  const handleSubscribeUser = async (userId) => {
    try {
      await subscribeUser(userId);
      toast({ title: 'User subscribed', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      const msg = err?.message || err?.details || 'Subscribe failed';
      toast({ title: 'Subscribe failed', description: msg, status: 'error', duration: 4000, isClosable: true });
    }
  };

  const handleToggleUser = async (userCampaignId, isEnabled) => {
    try {
      await toggleUser(userCampaignId, isEnabled);
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleUnsubscribeUser = async (userCampaignId) => {
    try {
      await unsubscribeUser(userCampaignId);
      toast({ title: 'User unsubscribed', status: 'info', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Unsubscribe failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleDeployAll = async () => {
    const unsubscribedIds = mergedUsers
      .filter((u) => u.status === 'not_subscribed')
      .map((u) => u.uid);
    if (!unsubscribedIds.length) {
      toast({ title: 'All users are already subscribed', status: 'info', duration: 2000, isClosable: true });
      return;
    }
    try {
      const result = await bulkSubscribe(unsubscribedIds);
      toast({
        title: 'Deploy complete',
        description: `${result.succeeded} subscribed, ${result.failed} failed`,
        status: result.failed ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Deploy failed', description: err.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  const handleBulkSubscribeSelected = async () => {
    const ids = [...selectedIds].filter((uid) => {
      const u = mergedUsers.find((m) => m.uid === uid);
      return u && u.status === 'not_subscribed';
    });
    if (!ids.length) {
      toast({ title: 'No unsubscribed users selected', status: 'info', duration: 2000, isClosable: true });
      return;
    }
    const result = await bulkSubscribe(ids);
    setSelectedIds(new Set());
    toast({
      title: `Subscribed ${result.succeeded} user(s)`,
      description: result.failed ? `${result.failed} failed` : undefined,
      status: result.failed ? 'warning' : 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleBulkEnable = async () => {
    const ids = [...selectedIds]
      .map((uid) => subsByUserId.get(uid))
      .filter((s) => s && !s.isEnabled)
      .map((s) => s.id);
    if (!ids.length) return;
    try {
      await bulkToggle(ids, true);
      setSelectedIds(new Set());
      toast({ title: `Enabled ${ids.length} campaign(s)`, status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Enable failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleBulkDisable = async () => {
    const ids = [...selectedIds]
      .map((uid) => subsByUserId.get(uid))
      .filter((s) => s && s.isEnabled)
      .map((s) => s.id);
    if (!ids.length) return;
    try {
      await bulkToggle(ids, false);
      setSelectedIds(new Set());
      toast({ title: `Disabled ${ids.length} campaign(s)`, status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Disable failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleEnableAll = async () => {
    const ids = subscribers.filter((s) => !s.isEnabled).map((s) => s.id);
    if (!ids.length) return;
    try {
      await bulkToggle(ids, true);
      toast({ title: `Enabled ${ids.length} campaign(s)`, status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Enable all failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleDisableAll = async () => {
    const ids = subscribers.filter((s) => s.isEnabled).map((s) => s.id);
    if (!ids.length) return;
    try {
      await bulkToggle(ids, false);
      toast({ title: `Disabled ${ids.length} campaign(s)`, status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Disable all failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const statusBadge = (status) => {
    if (status === 'enabled') return <Badge colorScheme="green" fontSize="xs">Enabled</Badge>;
    if (status === 'disabled') return <Badge colorScheme="gray" fontSize="xs">Disabled</Badge>;
    return <Badge variant="outline" colorScheme="gray" fontSize="xs">Not Subscribed</Badge>;
  };

  const hasSelection = selectedIds.size > 0;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pb={2}>
          <VStack align="start" spacing={1}>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontSize="lg" fontWeight="bold">{template?.name || 'Campaign'}</Text>
              <Badge colorScheme={template?.isActive ? 'green' : 'gray'} fontSize="xs">
                {template?.isActive ? 'Active' : 'Inactive'}
              </Badge>
              <Badge colorScheme="purple" fontSize="xs">
                v{template?.version || 1}
              </Badge>
            </HStack>
            <Text fontSize="xs" color={subText}>
              {getTriggerDescription(template?.triggerType, template?.triggerConfig)}
            </Text>
            <HStack spacing={1} pt={1}>
              <Tooltip label="Edit in builder">
                <IconButton
                  as={RouterLink}
                  to={`/crm/campaigns/builder/${template?.id}`}
                  icon={<FiEdit2 />}
                  size="xs"
                  variant="ghost"
                  aria-label="Edit"
                />
              </Tooltip>
              <Tooltip label="Settings">
                <IconButton
                  icon={<FiSettings />}
                  size="xs"
                  variant="ghost"
                  aria-label="Settings"
                  onClick={() => onSettings?.(template)}
                />
              </Tooltip>
              <Tooltip label="View logs">
                <IconButton
                  icon={<FiActivity />}
                  size="xs"
                  variant="ghost"
                  aria-label="View Logs"
                  onClick={() => onViewLogs?.({ templateId: template?.id, name: template?.name })}
                />
              </Tooltip>
              <Tooltip label="Refresh subscribers">
                <IconButton
                  icon={<FiRefreshCw />}
                  size="xs"
                  variant="ghost"
                  aria-label="Refresh"
                  onClick={reloadSubs}
                  isLoading={subsLoading}
                />
              </Tooltip>
            </HStack>
          </VStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={5}>
            {/* Stats Bar */}
            <StatGroup>
              <StatCard label="Total Users" value={counts.total} />
              <StatCard label="Subscribed" value={counts.enabled + counts.disabled} color="blue.500" />
              <StatCard label="Enabled" value={counts.enabled} color="green.500" />
              <StatCard label="Pending Queue" value={template?.pendingQueueItems ?? '—'} color="yellow.600" />
              <StatCard label="Completed" value={template?.completedQueueItems ?? '—'} color="green.600" />
              <StatCard label="Failed" value={template?.failedQueueItems ?? '—'} color="red.500" />
            </StatGroup>

            {subsError && (
              <Alert status="error" borderRadius="md" size="sm">
                <AlertIcon />
                {subsError}
              </Alert>
            )}

            {/* Bulk Progress */}
            {bulkProgress && (
              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="xs" fontWeight="medium">
                    Deploying... {bulkProgress.done}/{bulkProgress.total}
                    {bulkProgress.failed > 0 && ` (${bulkProgress.failed} failed)`}
                  </Text>
                  <Button size="xs" variant="ghost" colorScheme="red" onClick={cancelBulk}>
                    Cancel
                  </Button>
                </HStack>
                <Progress
                  value={(bulkProgress.done / bulkProgress.total) * 100}
                  size="sm"
                  borderRadius="full"
                  colorScheme={bulkProgress.failed > 0 ? 'yellow' : 'blue'}
                  hasStripe
                  isAnimated
                />
              </Box>
            )}

            {/* Bulk Actions Bar */}
            <Flex gap={2} flexWrap="wrap" align="center">
              <Button
                size="sm"
                leftIcon={<FiUploadCloud />}
                colorScheme="blue"
                onClick={handleDeployAll}
                isDisabled={!!bulkProgress || counts.notSubscribed === 0}
              >
                Deploy to All ({counts.notSubscribed})
              </Button>
              <Button
                size="sm"
                leftIcon={<FiToggleRight />}
                variant="outline"
                colorScheme="green"
                onClick={handleEnableAll}
                isDisabled={!!bulkProgress || counts.disabled === 0}
              >
                Enable All
              </Button>
              <Button
                size="sm"
                leftIcon={<FiToggleLeft />}
                variant="outline"
                colorScheme="gray"
                onClick={handleDisableAll}
                isDisabled={!!bulkProgress || counts.enabled === 0}
              >
                Disable All
              </Button>
            </Flex>

            {/* Selection Bulk Actions */}
            {hasSelection && (
              <HStack
                bg={selectionBg}
                px={3}
                py={2}
                borderRadius="md"
                spacing={2}
              >
                <Text fontSize="xs" fontWeight="medium">
                  {selectedIds.size} selected
                </Text>
                <Button size="xs" colorScheme="blue" onClick={handleBulkSubscribeSelected}>
                  Subscribe
                </Button>
                <Button size="xs" colorScheme="green" variant="outline" onClick={handleBulkEnable}>
                  Enable
                </Button>
                <Button size="xs" variant="outline" onClick={handleBulkDisable}>
                  Disable
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                  Clear
                </Button>
              </HStack>
            )}

            {/* Search & Filter */}
            <HStack spacing={2}>
              <InputGroup size="sm" flex={1}>
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray" />
                </InputLeftElement>
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  borderRadius="md"
                />
              </InputGroup>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  fontSize: '0.875rem',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'inherit',
                  background: 'transparent',
                }}
              >
                <option value="all">All ({counts.total})</option>
                <option value="enabled">Enabled ({counts.enabled})</option>
                <option value="disabled">Disabled ({counts.disabled})</option>
                <option value="not_subscribed">Not Subscribed ({counts.notSubscribed})</option>
              </select>
            </HStack>

            {/* User Table */}
            {loading ? (
              <Flex justify="center" py={10}><Spinner /></Flex>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th w="40px" px={2}>
                        <Checkbox
                          size="sm"
                          isChecked={selectedIds.size === filtered.length && filtered.length > 0}
                          isIndeterminate={selectedIds.size > 0 && selectedIds.size < filtered.length}
                          onChange={toggleSelectAll}
                        />
                      </Th>
                      <Th>User</Th>
                      <Th>Role</Th>
                      <Th>Status</Th>
                      <Th>Version</Th>
                      <Th w="120px" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filtered.map((u) => {
                      const sub = u.subscription;
                      const isBusy = busyIds.has(u.uid) || busyIds.has(sub?.id);
                      return (
                        <Tr key={u.uid} _hover={{ bg: hoverBg }}>
                          <Td px={2}>
                            <Checkbox
                              size="sm"
                              isChecked={selectedIds.has(u.uid)}
                              onChange={() => toggleSelect(u.uid)}
                            />
                          </Td>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">{u.name || 'Unknown'}</Text>
                              <Text fontSize="xs" color={subText}>{u.email}</Text>
                            </VStack>
                          </Td>
                          <Td>
                            <Text fontSize="xs" color={subText}>{u.crmRole || '—'}</Text>
                          </Td>
                          <Td>{statusBadge(u.status)}</Td>
                          <Td>
                            <Text fontSize="xs" color={subText}>
                              {sub ? `v${sub.templateVersion || '?'}` : '—'}
                            </Text>
                          </Td>
                          <Td>
                            {!sub ? (
                              <Button
                                size="xs"
                                colorScheme="blue"
                                leftIcon={<FiUserPlus />}
                                isLoading={isBusy}
                                onClick={() => handleSubscribeUser(u.uid)}
                              >
                                Subscribe
                              </Button>
                            ) : (
                              <HStack spacing={1}>
                                <Tooltip label={sub.isEnabled ? 'Disable' : 'Enable'}>
                                  <span>
                                    <Switch
                                      size="sm"
                                      colorScheme="green"
                                      isChecked={sub.isEnabled}
                                      isDisabled={isBusy}
                                      onChange={() => handleToggleUser(sub.id, !sub.isEnabled)}
                                    />
                                  </span>
                                </Tooltip>
                                <Menu>
                                  <MenuButton
                                    as={IconButton}
                                    icon={<FiMoreVertical />}
                                    size="xs"
                                    variant="ghost"
                                    aria-label="More"
                                    isDisabled={isBusy}
                                  />
                                  <MenuList fontSize="sm" minW="140px">
                                    <MenuItem
                                      icon={<FiUserMinus />}
                                      color="red.500"
                                      onClick={() => handleUnsubscribeUser(sub.id)}
                                    >
                                      Unsubscribe
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              </HStack>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <Tr>
                        <Td colSpan={6} textAlign="center" py={6}>
                          <Text fontSize="sm" color="gray.500">
                            {search || statusFilter !== 'all'
                              ? 'No users match the current filter.'
                              : 'No CRM users found.'}
                          </Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
